import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import type { AppUser } from '@/api/authStorage';
import { clearStoredSession, getStoredSession, setStoredSession, type StoredAuthSession } from '@/api/authStorage';
import { canAccessProfile, hasPermission, isSuperAdmin, normalizeAuthUser } from '@/api/authUser';
import {
  authClearClientSession,
  authFetchMe,
  authGoogleAuthorizeUrl,
  authGoogleExchange,
  authLogin,
  authSignup,
  authCompleteProfile,
} from '@/api/client';

type AuthContextValue = {
  user: AppUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  hasPermission: (code: string) => boolean;
  canAccessProfile: (profileId: string) => boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  completeOAuthLogin: (code: string) => Promise<{ error: Error | null }>;
  completeProfile: (input: {
    firstName: string;
    lastName: string;
    companyName: string;
  }) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function sessionFromMe(
  stored: StoredAuthSession,
  meUser: Record<string, unknown>
): StoredAuthSession {
  return {
    ...stored,
    user: normalizeAuthUser(meUser),
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<StoredAuthSession | null>(null);
  const [loading, setLoading] = useState(true);

  const resetClientCaches = useCallback(() => {
    queryClient.clear();
  }, [queryClient]);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      const stored = getStoredSession();
      if (!stored?.accessToken) {
        if (!cancelled) {
          setSession(null);
          setLoading(false);
        }
        return;
      }
      try {
        const me = await authFetchMe(stored.accessToken);
        const next = sessionFromMe(stored, me.user as Record<string, unknown>);
        if (next.user.is_active === false) {
          clearStoredSession();
          if (!cancelled) setSession(null);
          return;
        }
        setStoredSession(next);
        if (!cancelled) setSession(next);
      } catch (e) {
        if (axios.isAxiosError(e) && e.response?.status === 503) {
          if (!cancelled) setSession(stored);
        } else {
          clearStoredSession();
          if (!cancelled) setSession(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void init();
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      resetClientCaches();
      const next = await authLogin(email, password);
      setStoredSession(next);
      setSession(next);
      return { error: null as Error | null };
    } catch (e) {
      const msg = axiosErrorDetail(e);
      return { error: new Error(msg ?? 'Sign in failed') };
    }
  }, [resetClientCaches]);

  const signUp = useCallback(async (email: string, password: string) => {
    try {
      resetClientCaches();
      const next = await authSignup(email, password);
      setStoredSession(next);
      setSession(next);
      return { error: null as Error | null };
    } catch (e) {
      const msg = axiosErrorDetail(e);
      return { error: new Error(msg ?? 'Sign up failed') };
    }
  }, [resetClientCaches]);

  const signInWithGoogle = useCallback(async () => {
    try {
      const url = await authGoogleAuthorizeUrl();
      window.location.assign(url);
      return { error: null as Error | null };
    } catch (e) {
      const msg = axiosErrorDetail(e);
      return { error: new Error(msg ?? 'Google sign-in is unavailable') };
    }
  }, []);

  const completeOAuthLogin = useCallback(async (code: string) => {
    try {
      resetClientCaches();
      const next = await authGoogleExchange(code);
      setStoredSession(next);
      setSession(next);
      return { error: null as Error | null };
    } catch (e) {
      const msg = axiosErrorDetail(e);
      return { error: new Error(msg ?? 'Google sign-in failed') };
    }
  }, [resetClientCaches]);

  const completeProfile = useCallback(
    async (input: { firstName: string; lastName: string; companyName: string }) => {
      const token = session?.accessToken;
      if (!token) {
        return { error: new Error('Sign in required') };
      }
      try {
        const me = await authCompleteProfile(token, input);
        const next = sessionFromMe({ ...session, accessToken: token }, me.user as Record<string, unknown>);
        if (!next.user.terms_accepted_at) {
          next.user = { ...next.user, terms_accepted_at: new Date().toISOString() };
        }
        if (!next.user.profile_completed_at) {
          next.user = { ...next.user, profile_completed_at: new Date().toISOString() };
        }
        setStoredSession(next);
        setSession(next);
        return { error: null as Error | null };
      } catch (e) {
        const msg = axiosErrorDetail(e);
        return { error: new Error(msg ?? 'Could not save your details') };
      }
    },
    [session]
  );

  const signOut = useCallback(async () => {
    authClearClientSession();
    resetClientCaches();
    setSession(null);
  }, [resetClientCaches]);

  const user = session?.user ?? null;

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      isAuthenticated: Boolean(session?.accessToken),
      isSuperAdmin: isSuperAdmin(user),
      hasPermission: (code: string) => hasPermission(user, code),
      canAccessProfile: (profileId: string) => canAccessProfile(user, profileId),
      signIn,
      signUp,
      signInWithGoogle,
      completeOAuthLogin,
      completeProfile,
      signOut,
    }),
    [user, loading, session?.accessToken, signIn, signUp, signInWithGoogle, completeOAuthLogin, completeProfile, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

function axiosErrorDetail(error: unknown): string | null {
  if (!axios.isAxiosError(error)) return null;
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return error.message;
}
